import { z } from 'zod';

/**
 * Request schema for API key exchange
 * The API key is passed in the Authorization header as: Bearer <api-key>
 */
export const ExchangeAccessKeyRequestSchema = z
  .object({
    authorization: z
      .string()
      .describe('Authorization header containing: Bearer <your-api-key>'),
  })
  .describe('API key exchange request headers');

/**
 * Response schema for successful API key exchange
 */
export const ExchangeAccessKeyResponseSchema = z
  .object({
    jwt: z
      .string()
      .describe(
        'JWT bearer token to use for authenticated API requests. Include in Authorization header as: Bearer <jwt>',
      ),
  })
  .describe('API key exchange response containing JWT token');

export const DescopePublicAuthConfigSchema = z
  .object({
    projectId: z
      .string()
      .describe(
        'Public Descope project identifier used by clients to initialize the SDK',
      ),
    baseUrl: z
      .string()
      .optional()
      .describe(
        'Optional Descope API base URL for region-specific deployments (e.g. EU)',
      ),
  })
  .describe('Public Descope configuration');

export const OpenIdPublicAuthConfigSchema = z
  .object({
    issuerUrl: z
      .string()
      .describe('Public OpenID issuer URL used for discovery'),
    clientId: z
      .string()
      .describe('Public OpenID client identifier used by clients'),
    scopes: z
      .string()
      .describe('Space-delimited scopes requested during the OAuth flow'),
    logoutUri: z
      .string()
      .optional()
      .describe('Optional post-logout redirect URI'),
    cookieDomain: z
      .string()
      .optional()
      .describe(
        'Optional cookie domain used by the gateway for OIDC session cookies',
      ),
  })
  .describe('Public OpenID configuration');

export const PortalUrlsSchema = z.object({
  portalAppUrl: z
    .string()
    .optional()
    .describe('Base URL of the portal (UI) Next.js app for this deployment'),
});

export const GetAuthConfigResponseSchema = z
  .discriminatedUnion('authProvider', [
    z
      .object({
        authProvider: z.literal('descope'),
        descope: DescopePublicAuthConfigSchema,
      })
      .extend(PortalUrlsSchema.shape),
    z
      .object({
        authProvider: z.literal('openid'),
        openid: OpenIdPublicAuthConfigSchema,
      })
      .extend(PortalUrlsSchema.shape),
    z
      .object({
        authProvider: z.literal('local'),
      })
      .extend(PortalUrlsSchema.shape),
  ])
  .describe('Public authentication bootstrap configuration for clients');

export type GetAuthConfigResponse = z.infer<typeof GetAuthConfigResponseSchema>;

/**
 * Error response for invalid API key
 */
export const UnauthorizedErrorSchema = z
  .object({
    statusCode: z.literal(401).describe('HTTP status code'),
    message: z.string().describe('Error message'),
    error: z.string().optional().describe('Error type'),
  })
  .describe('Unauthorized error response');

/**
 * Single tenant info with user's roles and permissions
 */
export const UserTenantSchema = z.object({
  id: z.string().describe('Unique tenant identifier'),
  name: z.string().describe('Display name of the tenant'),
  roles: z.array(z.string()).describe("User's roles in this tenant"),
  permissions: z
    .array(z.string())
    .describe("User's permissions in this tenant"),
  isSubTenant: z.boolean().describe('Whether this is a sub-tenant'),
  parentId: z.string().nullable().describe('Parent tenant ID if sub-tenant'),
});

export type UserTenant = z.infer<typeof UserTenantSchema>;

/**
 * Response schema for getting user tenants
 */
export const GetUserTenantsResponseSchema = z
  .object({
    tenants: z.array(UserTenantSchema).describe('List of accessible tenants'),
    currentTenantId: z.string().describe('Currently active tenant ID'),
    isTerraUser: z
      .boolean()
      .describe(
        'Whether the user is a Terra internal user (god mode). These users bypass all permission checks.',
      ),
    hasAccessToTerraTenant: z
      .boolean()
      .describe(
        'Whether the user can access Terra testers tenant (authenticated as it or has it in accessible tenants). Used for feature-flag management access.',
      ),
  })
  .describe('User tenants response');

export type GetUserTenantsResponse = z.infer<
  typeof GetUserTenantsResponseSchema
>;

export const GetByocTenantAttributeResponseSchema = z
  .object({
    byoc: z
      .boolean()
      .describe(
        'Whether the current tenant custom attribute marks it for bring-your-own-cloud deployment',
      ),
  })
  .describe('Current tenant bring-your-own-cloud attribute');

export type GetByocTenantAttributeResponse = z.infer<
  typeof GetByocTenantAttributeResponseSchema
>;

/**
 * Sub-tenant info for management
 */
export const SubTenantSchema = z.object({
  id: z.string().describe('Unique tenant identifier'),
  name: z.string().describe('Display name of the sub-tenant'),
  userCount: z.number().describe('Number of users in the sub-tenant'),
});

export type SubTenant = z.infer<typeof SubTenantSchema>;

/**
 * Response schema for listing sub-tenants
 */
export const GetSubTenantsResponseSchema = z
  .object({
    subTenants: z.array(SubTenantSchema).describe('List of sub-tenants'),
  })
  .describe('Sub-tenants list response');

export type GetSubTenantsResponse = z.infer<typeof GetSubTenantsResponseSchema>;

/**
 * Request schema for creating a sub-tenant
 */
export const CreateSubTenantRequestSchema = z.object({
  name: z.string().min(1).max(100).describe('Display name for the sub-tenant'),
});

export type CreateSubTenantRequest = z.infer<
  typeof CreateSubTenantRequestSchema
>;

/**
 * Response schema for creating a sub-tenant
 */
export const CreateSubTenantResponseSchema = z
  .object({
    id: z.string().describe('Created sub-tenant ID'),
    name: z.string().describe('Created sub-tenant name'),
  })
  .describe('Created sub-tenant response');

export type CreateSubTenantResponse = z.infer<
  typeof CreateSubTenantResponseSchema
>;

/**
 * Request schema for inviting a user to a sub-tenant
 */
export const InviteUserToSubTenantRequestSchema = z.object({
  email: z.email().describe('Email address of the user to invite'),
  roleNames: z
    .array(z.string())
    .optional()
    .describe('Roles to assign to the user'),
});

export type InviteUserToSubTenantRequest = z.infer<
  typeof InviteUserToSubTenantRequestSchema
>;

/**
 * Response schema for inviting a user
 */
export const InviteUserResponseSchema = z
  .object({
    success: z.boolean().describe('Whether the invitation was sent'),
    userId: z.string().optional().describe('User ID if user already exists'),
  })
  .describe('User invitation response');

export type InviteUserResponse = z.infer<typeof InviteUserResponseSchema>;

/**
 * A single tenant in the platform (for Terra admin use)
 */
export const PlatformTenantSchema = z.object({
  id: z.string().describe('Unique tenant identifier'),
  name: z.string().describe('Display name of the tenant'),
  isSubTenant: z.boolean().describe('Whether this is a sub-tenant'),
  parentId: z.string().nullable().describe('Parent tenant ID if sub-tenant'),
});

export type PlatformTenant = z.infer<typeof PlatformTenantSchema>;

/**
 * Response schema for getting all platform tenants (Terra admin only)
 */
export const GetAllPlatformTenantsResponseSchema = z
  .object({
    tenants: z
      .array(PlatformTenantSchema)
      .describe('All tenants in the platform'),
  })
  .describe('All platform tenants response');

export type GetAllPlatformTenantsResponse = z.infer<
  typeof GetAllPlatformTenantsResponseSchema
>;

/**
 * Error response for forbidden operations
 */
export const ForbiddenErrorSchema = z
  .object({
    statusCode: z.literal(403).describe('HTTP status code'),
    message: z.string().describe('Error message'),
    error: z.string().optional().describe('Error type'),
  })
  .describe('Forbidden error response');

/**
 * Request schema for OAuth code exchange
 * Used to exchange authorization code for tokens via backend proxy
 */
export const ExchangeCodeRequestSchema = z.object({
  code: z.string().describe('Authorization code from OAuth provider'),
  code_verifier: z
    .string()
    .describe('PKCE code verifier used in authorization request'),
  redirect_uri: z
    .string()
    .describe('Redirect URI used in authorization request'),
});

export type ExchangeCodeRequest = z.infer<typeof ExchangeCodeRequestSchema>;

/**
 * Response schema for OAuth code exchange (legacy; tokens in body).
 * Prefer cookie-based session: gateway sets HttpOnly cookies and returns OidcSessionResponseSchema.
 */
export const ExchangeCodeResponseSchema = z.object({
  access_token: z.string().describe('Access token for API requests'),
  id_token: z.string().describe('ID token containing user information'),
  refresh_token: z
    .string()
    .optional()
    .describe('Refresh token for obtaining new access tokens'),
  expires_in: z.number().describe('Token expiration time in seconds'),
  token_type: z.string().describe('Token type (usually "Bearer")'),
});

export type ExchangeCodeResponse = z.infer<typeof ExchangeCodeResponseSchema>;

/**
 * User profile returned when using HttpOnly cookie session (no tokens in response body).
 */
export const OidcProfileSchema = z.object({
  sub: z.string().describe('Subject / user ID'),
  email: z.email().optional().describe('User email'),
  name: z.string().optional().describe('Display name'),
  picture: z.url().optional().describe('Profile picture URL'),
});

export type OidcProfile = z.infer<typeof OidcProfileSchema>;

/**
 * Response schema for OAuth exchange/refresh when using HttpOnly cookies.
 * Tokens are set in cookies; body contains only profile and expires_in.
 */
export const OidcSessionResponseSchema = z.object({
  profile: OidcProfileSchema.describe('User profile from ID token'),
  expires_in: z.number().describe('Token expiration time in seconds'),
});

export type OidcSessionResponse = z.infer<typeof OidcSessionResponseSchema>;

/**
 * Request schema for OAuth token refresh.
 * refresh_token is optional when using cookie-based session (gateway reads from cookie).
 */
export const RefreshTokenRequestSchema = z.object({
  refresh_token: z
    .string()
    .optional()
    .describe(
      'Refresh token (optional when session uses HttpOnly cookie; gateway reads from cookie)',
    ),
});

export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

export const LocalLoginRequestSchema = z.object({
  username: z.string().min(1).max(256),
  password: z.string().min(1).max(256),
});

export type LocalLoginRequest = z.infer<typeof LocalLoginRequestSchema>;

export const LocalLoginTokenResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  token_type: z.literal('Bearer'),
});

export type LocalLoginTokenResponse = z.infer<
  typeof LocalLoginTokenResponseSchema
>;
